import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  applyRideStatusUpdates,
  normalizeRideEntity,
  tableRowsAtomFamily,
  type IRideStatusUpdate,
  type IRideTableDto,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";
import { useStarredRows } from "../dynamic-tables";
import { useHighlightEdited } from "../use-highlight-edited";

export const useRidesListActions = () => {
  const { entity: entityFromContext } = usePageContext();
  const tableEntity = normalizeRideEntity(entityFromContext);
  const [rides, setRides] = useRecoilState(tableRowsAtomFamily(tableEntity));
  const { setHighlightedRows } = useHighlightEdited(tableEntity);
  const { removeStarredIds } = useStarredRows(tableEntity);

  const addRide = useEvent((ride: IRideTableDto): void => {
    setRides([...(rides || []), { ...ride }]);

    if (ride?.id) {
      setHighlightedRows([ride.id]);
    }
  });

  const replaceRide = useEvent((rideUpdated: IRideTableDto): void => {
    const updatedRides = rides?.map(ride => {
      return ride.id === rideUpdated.id ? rideUpdated : ride;
    });

    setRides(updatedRides);

    if (rideUpdated?.id) {
      setHighlightedRows([rideUpdated.id]);
    }
  });

  const mergeRideRow = useEvent((rideUpdated: IRideTableDto): void => {
    const updatedRides = rides?.map(ride => {
      return ride.id === rideUpdated.id ? { ...ride, ...rideUpdated } : ride;
    });

    setRides(updatedRides);

    if (rideUpdated?.id) {
      setHighlightedRows([rideUpdated.id]);
    }
  });

  const replaceRides = useEvent((ridesUpdated: IRideTableDto[]): void => {
    const ridesUpdatedMap = new Map(ridesUpdated.map(e => [e.id, e]));

    const updatedRides = rides?.map(ride => {
      const updatedRide = ridesUpdatedMap.get(ride.id);
      return updatedRide ? updatedRide : ride;
    });

    setRides(updatedRides);

    if (ridesUpdated.length) {
      setHighlightedRows(ridesUpdated.map(x => x.id));
    }
  });

  const removeRide = useEvent((rideId: number): void => {
    const updatedRides = rides?.filter(ride => ride.id !== rideId);
    setRides(updatedRides);
    removeStarredIds([rideId]);
  });

  const removeRides = useEvent((rideIds: number[]): void => {
    const rideIdsSet = new Set(rideIds);
    const updatedRides = rides?.filter(ride => !rideIdsSet.has(ride.id));
    setRides(updatedRides);
    removeStarredIds(rideIds);
  });

  const updateRidesStatus = useEvent((ridesUpdate: IRideStatusUpdate[]) => {
    const { updatedRows, hasMatchedRows } = applyRideStatusUpdates({
      rows: rides ?? [],
      updates: ridesUpdate,
    });

    // Flag is used to avoid re-renders without changes
    if (hasMatchedRows) {
      setRides(updatedRows);
    }
  });

  return {
    rides,
    addRide,
    replaceRide,
    mergeRideRow,
    replaceRides,
    removeRide,
    removeRides,
    updateRidesStatus,
  };
};
